import React, { useState, useEffect } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { Button, Dialog } from '@betareader/design-system';

// i18n
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// redux
import { useDispatch, useSelector } from 'react-redux';

import {
  saveBook,
  fetchCurrentBook
} from '../../../modules/book';

import { imageUtil, accessUtil, authUtil } from '../../../utils';
import CoverFileUpload from './coverFileUpload';
import styled from 'styled-components';

const CoverUploadArea = styled.div`
  background: var(--br-surface);
  color: var(--br-text);
  border: 1px dashed var(--br-border);
  height: 100%;
  width: min(280px, calc(100vw - 32px));

  .dropzone {
    background: transparent !important;
    border: 0 !important;
  }
  .dropzone, .dz-default, .dz-message {
    color: inherit;
  }
`;

const CoverPreview = styled.div`
  position: relative;
  display: inline-flex;
  height: 200px;

  img { height: 200px; width: auto; max-width: 100%; object-fit: contain; }
  button { position: absolute; top: 0.25rem; right: 0.25rem; }
`;

export const BookCover = ({
  t
}) => {
  const currentBook = useSelector(state => state.book.currentBook);
  const params = useParams();
  const dispatch = useDispatch();

  const [showDeleteCoverModal, setShowDeleteCoverModal] = useState(false);
  const [uploadActive, setUploadActive] = useState(false);

  const readOnly = !accessUtil.isAllowed(currentBook?.access, ['edit']);

  useEffect(() => {
    if (currentBook?.cover?._id === 'default_cover') {
      setUploadActive(true);
    }
  }, [currentBook?.cover?._id]);

  const deleteCover = async () => {
    const idToken = await authUtil.getFreshIdToken();
    if (!accessUtil.isAllowed(currentBook.access, ['edit'])) {
      return;
    }
    dispatch(saveBook(idToken, {
      _id: currentBook._id,
      cover: null
    }));
    setUploadActive(true);
  }

  const coverUploadComplete = async (response, error) => {
    // This should only handle success now since errors are handled by onError
    if (error) {
      console.error('Unexpected error in coverUploadComplete:', error);
      return;
    }
    
    // Handle upload success
    const idToken = await authUtil.getFreshIdToken();
    dispatch(fetchCurrentBook({ idToken, bookId: params.bookId }));
    setUploadActive(false);
  }

  const handleUploadError = (error) => {
    console.error('Cover upload error:', error);
    const errorMessage = error.message || 'Failed to upload cover image';
    toast.error(errorMessage);
    // Don't call coverUploadComplete here since it's handled separately
  }

  if (!currentBook) return null;

  const hasCustomCover = currentBook?.cover?._id !== 'default_cover' &&
    currentBook?.cover?.highRes;
  const coverUrl = hasCustomCover && imageUtil.getImageUrl({
    operation: 'height',
    size: 200,
    originalImageUrl: currentBook.cover.highRes.url
  });

  return (
    <div style={{ height: 200, padding: 0 }}>
      {
        !uploadActive && coverUrl && (
          <CoverPreview>
            <img src={coverUrl} alt={currentBook.title ? `Cover of ${currentBook.title}` : 'Book cover'} />
            {!readOnly && (
              <Button variant="secondary" size="small" aria-label={t('DeleteCover')} onClick={() => setShowDeleteCoverModal(true)}>
                {t('DeleteCover')}
              </Button>
            )}
          </CoverPreview>
        )
      }
      {
        uploadActive && (
          <CoverUploadArea>
            <CoverFileUpload
              style={{ display: 'inline-block' }}
              uploadMessage={t('CoverUploadMsg')}
              onComplete={coverUploadComplete}
              onError={handleUploadError}
            />
          </CoverUploadArea>
        )
      }
      <Dialog
        open={showDeleteCoverModal}
        title={t('DeleteCover')}
        onClose={() => setShowDeleteCoverModal(false)}
        actions={<>
          <Button variant="secondary" onClick={() => setShowDeleteCoverModal(false)}>{t('No')}</Button>
          <Button variant="danger" onClick={() => {
            setShowDeleteCoverModal(false);
            deleteCover();
          }}>{t('Yes')}</Button>
        </>}
      >
        <p>{t('DeleteCoverConfirmation')}</p>
      </Dialog>
    </div>
  );

}

export default withTranslation()(withRouter(BookCover));
